import React, { useState } from 'react';
import { X, Crown, Clock, Heart, Calendar, BarChart3, CreditCard, Smartphone, Globe, LogIn } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';
import { getProductInfo, formatPrice, createDirectCheckout } from '../../services/stripe';
import {
  isGooglePlayDemoMode,
  createDemoGooglePlayPurchase,
  purchaseGooglePlayProduct,
  completeGooglePlayPurchase,
  type GooglePlayProductSku
} from '../../services/googlePlay';
import { shouldUseStripePayments, shouldUseGooglePlayBilling, getPaymentMethodName } from '../../utils/platform';
import { logger } from '../../utils/logger';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: string;
}

export const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  onClose,
  feature = 'premium features'
}) => {
  const { startTrial, activatePremium, isTrialExpired, hasAccess, hasTrialBeenUsed } = useSubscription();
  const auth = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');
  const [isLoading, setIsLoading] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  // Platform detection
  const useStripe = shouldUseStripePayments();
  const useGooglePlay = shouldUseGooglePlayBilling();
  const paymentMethodName = getPaymentMethodName();

  const handleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await auth.signIn();
    } catch (error) {
      console.error('Sign-in failed:', error);
    } finally {
      setIsSigningIn(false);
    }
  };
  
  if (!isOpen) return null;
  
  const handleStartTrial = async () => {
    setIsLoading(true);
    try {
      await startTrial();
      onClose();
    } catch (error) {
      logger.error('Failed to start trial', error);
      alert('Failed to start trial. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleUpgradeToPremium = async () => {
    setIsLoading(true);

    try {
      if (useStripe) {
        // Web platform - use Stripe
        const productInfo = getProductInfo(selectedPlan === 'annual' ? 'yearly' : 'monthly');

        logger.debug('Starting Stripe checkout', {
          plan: selectedPlan,
          priceId: productInfo.priceId,
          amount: productInfo.price
        });

        // Redirect to Stripe checkout (payment confirmation happens on return)
        await createDirectCheckout(productInfo.priceId);
        // Note: Premium activation happens on successful return from Stripe
      } else if (useGooglePlay) {
        // Android platform - use Google Play Billing
        const sku: GooglePlayProductSku = selectedPlan === 'annual' ? 'yearly' : 'monthly';

        // Get user ID from auth context
        const userId = auth.user?.id || auth.user?.email;

        logger.debug('Starting Google Play purchase', { sku, plan: selectedPlan, userId });

        if (isGooglePlayDemoMode()) {
          // Demo mode for development/testing
          const success = await createDemoGooglePlayPurchase(sku, userId);
          if (success) {
            activatePremium();
            logger.debug('Demo Google Play purchase successful');
          }
        } else {
          // Real Google Play Billing
          try {
            const purchase = await purchaseGooglePlayProduct(sku, userId);
            await completeGooglePlayPurchase(purchase, userId);
            activatePremium();
            logger.debug('Google Play purchase completed successfully');
          } catch (purchaseError) {
            logger.error('Google Play purchase failed', purchaseError);
            throw purchaseError;
          }
        }
      } else {
        // Other platforms - show appropriate message
        alert(
          `Payment processing for ${paymentMethodName} is not yet implemented.\n\nFor now, enjoy all features for free!`
        );
        activatePremium();
      }
      
      logger.debug('Premium activated successfully');
      onClose();
      
      // In production, this would be:
      // await createDirectCheckout(productInfo.priceId);
      
    } catch (error) {
      logger.error('Payment error', error);
      alert('Payment failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const premiumFeatures = [
    {
      icon: <Heart className="w-5 h-5" />,
      title: "Complete 54-Day Novena Structure",
      description: "27 days of petition followed by 27 days of thanksgiving, perfectly organized"
    },
    {
      icon: <Calendar className="w-5 h-5" />,
      title: "Daily Prayer Guidance",
      description: "Step-by-step rosary prayers with rotating mysteries and traditional Catholic prayers"
    },
    {
      icon: <BarChart3 className="w-5 h-5" />,
      title: "Progress Tracking & Intentions",
      description: "Track completed days, set personal intentions, and maintain spiritual momentum"
    }
  ];
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto transition-colors duration-300">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-sacred-navy to-sacred-navy-deep rounded-t-2xl p-6 text-white border-b-2 border-sacred-gold">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="text-center">
            <div className="bg-white bg-opacity-20 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Crown className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {isTrialExpired ? 'Continue Your Journey' : 'Unlock Premium Features'}
            </h2>
            <p className="text-purple-100">
              {isTrialExpired 
                ? 'Your trial has ended. Subscribe to continue using all features.'
                : 'Start your 7-day free trial and deepen your prayer life.'
              }
            </p>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Premium Features */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              Premium Features Include:
            </h3>
            <div className="space-y-3">
              {premiumFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5">
                    {feature.icon}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                      {feature.title}
                    </div>
                    <div className="text-gray-600 dark:text-gray-300 text-xs">
                      {feature.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Platform Indicator */}
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <div className="flex items-center gap-2">
              {useGooglePlay ? <Smartphone className="w-4 h-4 text-blue-600 dark:text-blue-400" /> : <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
              <span className="text-sm text-blue-800 dark:text-blue-200">
                Payment via {paymentMethodName}
                {useGooglePlay && isGooglePlayDemoMode() && " (Demo Mode)"}
              </span>
            </div>
          </div>

          {/* Sign-in recommendation for mobile purchases */}
          {useGooglePlay && !auth.isAuthenticated && (
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
              <div className="flex items-start gap-2">
                <LogIn className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                    Sign in recommended
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    Sign in with Google to ensure your subscription is properly linked to your account.
                  </p>
                  <button
                    onClick={handleSignIn}
                    disabled={isSigningIn || auth.isLoading}
                    className="mt-2 flex items-center gap-2 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
                  >
                    <svg className="w-3 h-3" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {isSigningIn ? 'Signing in...' : 'Sign in with Google'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pricing Plans */}
          <div className="mb-6">
            <div className="grid grid-cols-1 gap-3">
              {/* Annual Plan */}
              <div 
                onClick={() => setSelectedPlan('annual')}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                  selectedPlan === 'annual'
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      selectedPlan === 'annual' 
                        ? 'border-purple-500 bg-purple-500' 
                        : 'border-gray-300 dark:border-gray-500'
                    }`}>
                      {selectedPlan === 'annual' && (
                        <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                      )}
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      Annual Plan
                    </span>
                  </div>
                  <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full text-xs font-medium">
                    Save 30%
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatPrice(getProductInfo('yearly').price)}<span className="text-sm font-normal text-gray-500"> / year</span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Just {formatPrice(getProductInfo('yearly').price / 12)} per month
                </div>
              </div>
              
              {/* Monthly Plan */}
              <div 
                onClick={() => setSelectedPlan('monthly')}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                  selectedPlan === 'monthly'
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedPlan === 'monthly' 
                      ? 'border-purple-500 bg-purple-500' 
                      : 'border-gray-300 dark:border-gray-500'
                  }`}>
                    {selectedPlan === 'monthly' && (
                      <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                    )}
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Monthly Plan
                  </span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatPrice(getProductInfo('monthly').price)}<span className="text-sm font-normal text-gray-500"> / month</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="space-y-3">
            {!hasTrialBeenUsed && !hasAccess && (
              <button
                onClick={handleStartTrial}
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-sacred-gold to-sacred-gold-light hover:from-sacred-gold-dark hover:to-sacred-gold text-gray-800 py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-102 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                    Starting trial...
                  </>
                ) : (
                  <>
                    <Clock className="w-5 h-5" />
                    Start 7-Day Free Trial
                  </>
                )}
              </button>
            )}
            
            <button
              onClick={handleUpgradeToPremium}
              disabled={isLoading}
              className="w-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                  Processing...
                </>
              ) : (
                <>
                  {useStripe ? (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Subscribe Now - {selectedPlan === 'annual' ? formatPrice(getProductInfo('yearly').price) + '/year' : formatPrice(getProductInfo('monthly').price) + '/month'}
                    </>
                  ) : useGooglePlay ? (
                    <>
                      <Smartphone className="w-5 h-5" />
                      Subscribe via Google Play - {selectedPlan === 'annual' ? formatPrice(getProductInfo('yearly').price) + '/year' : formatPrice(getProductInfo('monthly').price) + '/month'}
                    </>
                  ) : (
                    <>
                      <Crown className="w-5 h-5" />
                      Get Premium Access
                    </>
                  )}
                </>
              )}
            </button>
          </div>
          
          {/* Fine Print */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Cancel anytime. No commitment required.
              {!isTrialExpired && ' Trial includes full access to all features.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};